import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { flashcards } from '../data/flashcards';
import FlashcardCard from '../components/FlashcardCard';
import Resources from '../components/Resources';
import TypingHero from '../components/TypingHero';

const LandingPage: React.FC = () => {
  const [currentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  
  // Refs for smooth scrolling
  const featuresRef = useRef<HTMLElement>(null);
  const howItWorksRef = useRef<HTMLElement>(null);
  const testimonialsRef = useRef<HTMLElement>(null);
  const resourcesRef = useRef<HTMLElement>(null);
  
  const currentCard = flashcards[currentCardIndex];

  // Scroll function with momentum handling and position recovery
  const scrollToSection = useCallback((sectionId: string, event?: React.MouseEvent) => {
    // Prevent default behavior if event is provided
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    let targetRef: HTMLElement | null = null;
    
    switch (sectionId) {
      case 'features':
        targetRef = featuresRef.current;
        break;
      case 'how-it-works':
        targetRef = howItWorksRef.current;
        break;
      case 'testimonials':
        targetRef = testimonialsRef.current;
        break;
      case 'resources':
        targetRef = resourcesRef.current;
        break;
      default:
        return;
    }
    
    if (targetRef) {
      // Get the element's position and account for header height
      const elementTop = targetRef.offsetTop;
      const headerHeight = 80; // Header height in pixels
      const targetPosition = elementTop - headerHeight;
      
      // Smooth scroll to the target position
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
      
      // Fallback: If smooth scroll fails, try again after a short delay
      setTimeout(() => {
        const currentPosition = window.pageYOffset || document.documentElement.scrollTop;
        const tolerance = 50; // Allow 50px tolerance
        
        if (Math.abs(currentPosition - targetPosition) > tolerance) {
          console.log('Scroll retry triggered for section:', sectionId);
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }, 500);
    }
  }, []);
  
  const handleCardClick = () => {
    setShowAnswer(!showAnswer);
  };
  

  

  
  return (
    <div className="bg-gray-50" style={{ minHeight: '100vh', overflowY: 'auto' }}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity cursor-pointer">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs sm:text-sm">R</span>
              </div>
              <span className="text-lg sm:text-xl font-bold text-gray-900">RealEstart.io</span>
            </Link>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={(e) => scrollToSection('features', e)} className="text-gray-600 hover:text-[#FF6B6B] transition-colors">Features</button>
              <button onClick={(e) => scrollToSection('how-it-works', e)} className="text-gray-600 hover:text-[#FF6B6B] transition-colors">How It Works</button>
              <button onClick={(e) => scrollToSection('testimonials', e)} className="text-gray-600 hover:text-[#FF6B6B] transition-colors">Reviews</button>
              <Link to="/resources" className="text-gray-600 hover:text-[#FF6B6B] transition-colors">Resources</Link>
              <Link to="/auth" className="text-gray-600 hover:text-[#FF6B6B] transition-colors">Sign In</Link>
              <Link to="/demo" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg">Try Demo</Link>
            </div>
            
            {/* Mobile Navigation - Always show sign-in and demo buttons */}
            <div className="flex md:hidden items-center space-x-2">
              <Link to="/auth" className="text-gray-600 hover:text-[#FF6B6B] transition-colors text-sm px-3 py-2">Sign In</Link>
              <Link to="/demo" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg text-sm">Try Demo</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <TypingHero className="text-white" />
              <p className="text-xl mb-8 text-white/90">
                Transform your study sessions with our interactive flashcard app. 
                Practice with 500+ carefully crafted questions designed to help you 
                understand key concepts and ace your <span className="font-semibold">Florida, Georgia, California, Texas, New York, New Jersey</span> real estate exam with confidence.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link 
                  to="/checkout?plan=trial" 
                  className="bg-white text-[#FF6B6B] px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-sm sm:text-base font-semibold hover:shadow-lg transition-all transform hover:scale-105 text-center"
                >
                  Start 3-Day Trial
                </Link>
                <Link 
                  to="/checkout?plan=trial" 
                  className="border-2 border-white text-white px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-[#FF6B6B] transition-all text-center"
                >
                  Sign Up
                </Link>
                <Link 
                  to="/demo" 
                  className="border-2 border-white text-white px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-[#FF6B6B] transition-all text-center"
                >
                  Try Demo
                </Link>
              </div>
              <div className="mt-8 flex items-center justify-center lg:justify-start space-x-6 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-[#27AE60] rounded-full"></span>
                  <span>500+ State-Specific Flashcards</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-[#27AE60] rounded-full"></span>
                  <span>FL, GA, CA, TX, NY, NJ Available Now</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-[#27AE60] rounded-full"></span>
                  <span>3-Day Free Trial</span>
                </div>
              </div>
            </div>
                                    <div className="relative flex justify-center">
              <FlashcardCard
                card={currentCard}
                showAnswer={showAnswer}
                onCardClick={handleCardClick}
                onSwipe={() => {}} // No swipe functionality needed for homepage
                isAnimating={false}
                darkMode={true}
              />
              
              
            </div>
            

          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose RealEstart?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our interactive study platform is designed to make real estate education engaging, 
              effective, and accessible anywhere, anytime. <span className="font-semibold text-[#FF6B6B]">Currently available for Florida, Georgia, California, Texas, New York, and New Jersey</span> with more states coming soon!
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Smart Study System</h3>
              <p className="text-gray-600">
                Swipe right for concepts you know, left for those you need to review. 
                Track your progress and study at your own pace.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-[#FF8E53] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">📚</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Comprehensive Content</h3>
              <p className="text-gray-600">
                500+ flashcards covering all major real estate topics including 
                contracts, agency, property rights, state-specific laws, and more for each available state.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-[#2C3E50] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Study Anywhere</h3>
              <p className="text-gray-600">
                Works perfectly on desktop, tablet, and mobile. Study during 
                breaks, commutes, or whenever you have a few minutes.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-[#27AE60] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Track Your Progress</h3>
              <p className="text-gray-600">
                Monitor your performance with detailed statistics including 
                accuracy rates, study streaks, and improvement over time.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Quick & Efficient</h3>
              <p className="text-gray-600">
                Keyboard shortcuts and intuitive gestures make studying fast 
                and efficient. No time wasted navigating complex interfaces.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gray-50 hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-[#FF8E53] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl">🌙</span>
              </div>
              <h3 className="text-xl font-semibold mb-4">Dark Mode Support</h3>
              <p className="text-gray-600">
                Study comfortably in any lighting condition with our 
                dark mode feature. Easy on the eyes for late-night study sessions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Supported States Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Study for Your State's Real Estate Exam</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              We provide comprehensive study materials tailored to each state's specific real estate licensing requirements. 
              Choose your state and get started today!
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-12">
            {[
              { code: 'FL', name: 'Florida', available: true, featured: true },
              { code: 'GA', name: 'Georgia', available: true, featured: true },
              { code: 'CA', name: 'California', available: true, featured: true },
              { code: 'TX', name: 'Texas', available: true, featured: true },
              { code: 'NY', name: 'New York', available: true, featured: true },
              { code: 'NJ', name: 'New Jersey', available: true, featured: true },
              { code: 'IL', name: 'Illinois', available: false, featured: false },
              { code: 'PA', name: 'Pennsylvania', available: false, featured: false },
              { code: 'OH', name: 'Ohio', available: false, featured: false },
              { code: 'NC', name: 'North Carolina', available: false, featured: false },
              { code: 'MI', name: 'Michigan', available: false, featured: false },
              { code: 'VA', name: 'Virginia', available: false, featured: false }
            ].map((state) => (
              <div
                key={state.code}
                className={`p-4 rounded-xl text-center transition-all ${
                  state.available
                    ? 'bg-white/20 hover:bg-white/30 cursor-pointer'
                    : 'bg-white/10 opacity-60'
                } ${state.featured ? 'ring-2 ring-yellow-400' : ''}`}
              >
                <div className="text-2xl font-bold mb-1">{state.code}</div>
                <div className="text-sm text-blue-100">{state.name}</div>
                {state.available && (
                  <div className="text-xs text-green-300 mt-1">✓ Available</div>
                )}
                {!state.available && (
                  <div className="text-xs text-yellow-300 mt-1">Coming Soon</div>
                )}
                {state.featured && (
                  <div className="text-xs text-yellow-300 mt-1">⭐ Featured</div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <p className="text-blue-100 mb-6">
              Don't see your state? <span className="font-semibold">Contact us</span> to request support for your state's real estate exam!
            </p>
            <Link 
              to="/contact" 
              className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-all transform hover:scale-105 shadow-lg"
            >
              Request My State
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section ref={howItWorksRef} id="how-it-works" className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 mb-6">How RealEstart Transforms Your Study Experience</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover why students are choosing our effective approach to exam preparation. 
              It's not just studying—it's a complete learning transformation.
            </p>
          </div>
          
          {/* Step-by-Step Process */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-20">
            <div className="relative flex">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col h-full w-full">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold shadow-lg">
                  1
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Instant Access</h3>
                <p className="text-gray-600 text-center leading-relaxed flex-grow">
                  <strong>No registration required.</strong> Click "Start Studying Now" and immediately dive into your first question. 
                  Start with our comprehensive collection of real estate flashcards.
                </p>
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 font-semibold">✨ Pro Tip: Your progress is automatically saved, so you can pick up right where you left off.</p>
                </div>
              </div>
            </div>
            
            <div className="relative flex">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col h-full w-full">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF8E53] to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold shadow-lg">
                  2
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Smart Learning</h3>
                <p className="text-gray-600 text-center leading-relaxed flex-grow">
                  <strong>Our study system</strong> helps you learn effectively. 
                  Practice with flashcards, track your progress, and review concepts at your own pace.
                </p>
                <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-800 font-semibold">🎯 Result: More efficient learning compared to traditional study methods.</p>
                </div>
              </div>
            </div>
            
            <div className="relative flex">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col h-full w-full">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold shadow-lg">
                  3
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Master & Excel</h3>
                <p className="text-gray-600 text-center leading-relaxed flex-grow">
                  <strong>Watch your confidence soar</strong> as you track real-time progress with detailed analytics. 
                  See your accuracy improve, build study streaks, and identify exactly which topics need attention.
                </p>
                <div className="mt-6 p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-800 font-semibold">📈 Users report feeling more confident on exam day.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Image Section - Elegant Integration */}
          <div className="mb-16">
            <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                {/* Image - Always on top for mobile/tablet, right side for desktop */}
                <div className="relative order-1 lg:order-2">
                  <img 
                    src="/unnamed.png" 
                    alt="Professional real estate student using RealEstart platform" 
                    className="w-full h-full object-cover min-h-[400px] lg:min-h-[500px]"
                    loading="lazy"
                  />
                  
                  {/* Subtle overlay for better text readability on mobile */}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-transparent lg:hidden"></div>
                </div>
                
                {/* Content - Below image for mobile/tablet, left side for desktop */}
                <div className="p-8 lg:p-12 flex flex-col justify-center order-2 lg:order-1">
                  <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Real Students, Real Results</h3>
                  <p className="text-lg text-gray-600 mb-6">Join successful real estate professionals who've transformed their careers with our platform</p>
                  
                  {/* Call-to-Action */}
                  <div className="space-y-4">
                    <h4 className="text-xl font-semibold text-gray-800">Ready to Transform Your Study Experience?</h4>
                    <p className="text-gray-600">Join students already succeeding with RealEstart</p>
                    <Link 
                      to="/checkout?plan=trial" 
                      className="inline-block bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                    >
                      Start Your Free Trial
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why It Works */}
          <div className="bg-white rounded-3xl p-12 shadow-2xl border border-gray-100 mb-16">
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-gray-900 mb-4">Why RealEstart Works Better</h3>
              <p className="text-xl text-gray-600">Science-backed learning techniques that actually stick</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🧠</span>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Spaced Repetition</h4>
                <p className="text-sm text-gray-600">Questions you struggle with appear more frequently, ensuring you master difficult concepts.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚡</span>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Active Recall</h4>
                <p className="text-sm text-gray-600">Forcing your brain to retrieve information strengthens neural pathways and improves retention.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📊</span>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Progress Tracking</h4>
                <p className="text-sm text-gray-600">Real-time analytics help you identify weak areas and celebrate improvements.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Personalized Study</h4>
                <p className="text-sm text-gray-600">Study with flashcards organized by topic and track your progress over time.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Testimonials Section */}
      <section ref={testimonialsRef} id="testimonials" className="py-20 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-3xl font-bold mb-6 text-white">Join Successful Students</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="text-4xl font-bold mb-2 text-white">500+</div>
                <p className="text-lg text-white">Study Cards</p>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2 text-white">150+</div>
                <p className="text-lg text-white">Practice Questions</p>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2 text-white">3</div>
                <p className="text-lg text-white">Day Free Trial</p>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 lg:p-8 mb-8 border border-white/20 shadow-2xl">
              <div className="text-center">
                {/* Profile Avatar - Centered on mobile */}
                <div className="w-16 h-16 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-0 sm:mr-4 sm:ml-0 sm:float-left">
                  <span className="text-white text-2xl sm:text-xl font-bold">J</span>
                </div>
                
                {/* Quote Text - Better mobile layout */}
                <div className="sm:ml-16">
                  <p className="text-lg sm:text-xl lg:text-2xl xl:text-3xl mb-4 text-white font-medium leading-relaxed italic text-center sm:text-left">
                    "Before starting Realestart.io, I was completely overwhelmed by the dense material in my real estate course and struggling to stay motivated. The flashcards completely transformed my study routine. They break everything down into clear, concise points, which made complex topics finally click for me. Thanks to Realestart.io, I not only understood the material faster but also felt genuinely confident going into the exam. If you're serious about passing, this tool is an absolute must have!"
                  </p>
                  
                  {/* Rating and Author - Better mobile layout */}
                  <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start space-y-2 sm:space-y-0 sm:space-x-3">
                    <div className="flex space-x-1">
                      <span className="text-yellow-300 text-xl sm:text-2xl">★</span>
                      <span className="text-yellow-300 text-xl sm:text-2xl">★</span>
                      <span className="text-yellow-300 text-xl sm:text-2xl">★</span>
                      <span className="text-yellow-300 text-xl sm:text-2xl">★</span>
                      <span className="text-yellow-300 text-xl sm:text-2xl">★</span>
                    </div>
                    <span className="text-white text-base sm:text-lg font-semibold text-center sm:text-left whitespace-nowrap">- Justin C., Real Estate Student</span>
                  </div>
                </div>
              </div>
            </div>
            <Link to="/checkout?plan=trial" className="bg-white text-[#FF6B6B] px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 lg:py-4 rounded-full text-sm sm:text-base font-bold hover:shadow-xl transition-all transform hover:scale-105 inline-block">
              Start Your Success Story Today
            </Link>
          </div>
        </div>
      </section>

      {/* Resources Section */}
      <section ref={resourcesRef} id="resources">
        <Resources />
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
                      <h2 className="text-4xl font-bold mb-6">Ready to Ace Your Exam?</h2>
          <p className="text-xl mb-8 text-white/90">
            Join students who are already using RealEstart to prepare for their real estate exam. 
            Start your free study session today!
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/demo" className="bg-white text-[#FF6B6B] px-6 sm:px-8 lg:px-12 py-2.5 sm:py-3 lg:py-4 rounded-full text-sm sm:text-base font-semibold hover:shadow-lg transition-all transform hover:scale-105 inline-block">
              Try Demo - No Registration Required!
            </Link>
            <Link 
              to="/checkout?plan=trial"
              className="border-2 border-white text-white px-6 sm:px-8 lg:px-12 py-2.5 sm:py-3 lg:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-[#FF6B6B] transition-all transform hover:scale-105 inline-block"
            >
              Sign Up
            </Link>
          </div>
          <p className="mt-4 text-sm text-white/70">Try demo immediately or sign up for full access</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold mb-4">🏠 RealEstart.io</div>
              <p className="text-gray-400">
                Your trusted companion for real estate education and licensing preparation.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={(e) => scrollToSection('features', e)} className="hover:text-white transition-colors text-left">Features</button></li>
                <li><button onClick={(e) => scrollToSection('how-it-works', e)} className="hover:text-white transition-colors text-left">How It Works</button></li>
                <li><button onClick={(e) => scrollToSection('testimonials', e)} className="hover:text-white transition-colors text-left">Reviews</button></li>
                <li><Link to="/resources" className="hover:text-white transition-colors text-left">Resources</Link></li>
                <li><Link to="/demo" className="hover:text-white transition-colors text-left">Try Demo</Link></li>
                <li><Link to="/auth" className="hover:text-white transition-colors">Sign In</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
                <li><button className="hover:text-white transition-colors text-left">Feedback</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 RealEstart.io. This is a study aid and is not affiliated with any official exam provider.</p>
          </div>
        </div>
      </footer>


    </div>
  );
};

export default LandingPage;
